import { getAccessToken } from '../lib/authSession';
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Slider } from '../components/ui/slider';
import { Activity, Database, HardDrive, Cpu, RefreshCw, Bell, AlertTriangle, CheckCircle, Clock, Settings, History, ArrowLeft, Home } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HealthMonitorPage = () => {
  const [healthReport, setHealthReport] = useState(null);
  const [history, setHistory] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [showSettings, setShowSettings] = useState(false);
  const [error, setError] = useState(null);
  const [isAdmin, setIsAdmin] = useState(true);
  const navigate = useNavigate();

  const token = getAccessToken();

  const fetchHealthData = useCallback(async () => {
    if (!token) {
      setError('Por favor inicie sesión para acceder al monitor de salud');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/health-monitor/admin/report`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.status === 403) {
        navigate('/dashboard', { replace: true });
        return;
      } else if (response.ok) {
        const data = await response.json();
        setHealthReport(data.current);
        setHistory(data.history_24h || []);
        setAlerts(data.recent_alerts || []);
        setSettings(data.settings);
        setIsAdmin(true);
      } else if (response.status === 401) {
        setError('Sesión expirada. Por favor inicie sesión nuevamente.');
      }
    } catch (error) {
      console.error('Error fetching health data:', error);
      setError('Error al cargar datos de salud');
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => {
    fetchHealthData();
    const interval = setInterval(fetchHealthData, 60000);
    return () => clearInterval(interval);
  }, [fetchHealthData]);

  const runManualCheck = async () => {
    setRefreshing(true);
    try {
      const response = await fetch(`${API_URL}/api/health-monitor/admin/run-check`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setHealthReport(data.report);
        await fetchHealthData();
      }
    } catch (error) {
      console.error('Error running health check:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const sendTestAlert = async () => {
    try {
      const response = await fetch(`${API_URL}/api/health-monitor/admin/test-alert`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ message: 'Prueba de alerta de monitoreo de salud' })
      });
      
      if (response.ok) {
        const data = await response.json();
        alert(data.message);
      }
    } catch (error) {
      console.error('Error sending test alert:', error);
    }
  };

  const updateSettings = async (newSettings) => {
    try {
      const response = await fetch(`${API_URL}/api/health-monitor/admin/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newSettings)
      });
      
      if (response.ok) {
        const data = await response.json();
        setSettings(data.settings);
      }
    } catch (error) {
      console.error('Error updating settings:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy': return 'bg-green-500';
      case 'warning': return 'bg-yellow-500';
      case 'critical': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'healthy':
      case 'ok':
      case 'connected':
        return <Badge className="bg-green-100 text-green-800">OK</Badge>;
      case 'warning':
        return <Badge className="bg-yellow-100 text-yellow-800">Advertencia</Badge>;
      case 'critical':
      case 'disconnected':
        return <Badge className="bg-red-100 text-red-800">Crítico</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-800">Desconocido</Badge>;
    }
  };

  const formatUptime = (seconds) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-indigo-600" />
          <p className="text-gray-600">Cargando monitor de salud...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-red-500" />
          <p className="text-gray-600">{error}</p>
          <Button onClick={() => window.location.href = '/auth'} className="mt-4">
            Iniciar Sesión
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6" data-testid="health-monitor-page">
      {/* Back to Dashboard */}
      <div className="max-w-7xl mx-auto mb-4">
        <Link 
          to="/dashboard" 
          className="inline-flex items-center space-x-2 text-gray-500 hover:text-gray-900 transition-colors group"
        >
          <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
          <Home className="w-4 h-4" />
          <span>Volver al Dashboard</span>
        </Link>
      </div>
      
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Activity className="w-8 h-8 text-indigo-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Monitor de Salud del Sistema</h1>
              <p className="text-gray-500 text-sm">
                Última actualización: {healthReport ? new Date(healthReport.timestamp).toLocaleString() : 'N/A'}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {isAdmin && (
              <>
                <Button 
                  variant="outline" 
                  className="text-slate-800 border-slate-300 bg-white hover:bg-slate-100"
                  onClick={() => setShowSettings(!showSettings)}
                  data-testid="settings-btn"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Configuración
                </Button>
                <Button 
                  variant="outline" 
                  className="text-slate-800 border-slate-300 bg-white hover:bg-slate-100"
                  onClick={sendTestAlert}
                  data-testid="test-alert-btn"
                >
                  <Bell className="w-4 h-4 mr-2" />
                  Probar Alerta
                </Button>
                <Button 
                  onClick={runManualCheck} 
                  disabled={refreshing}
                  data-testid="refresh-btn"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                  {refreshing ? 'Verificando...' : 'Verificar Ahora'}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Overall Status Banner */}
      {healthReport && (
        <div className="max-w-7xl mx-auto mb-6">
          <div className={`${getStatusColor(healthReport.overall_status)} rounded-lg p-4 text-white`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {healthReport.overall_status === 'healthy' ? (
                  <CheckCircle className="w-6 h-6" />
                ) : (
                  <AlertTriangle className="w-6 h-6" />
                )}
                <span className="text-lg font-semibold">
                  {healthReport.overall_status === 'healthy' ? 'Sistema Funcionando Correctamente' :
                   healthReport.overall_status === 'warning' ? 'Sistema con Advertencias' :
                   'Sistema con Problemas Críticos'}
                </span>
              </div>
              <div className="flex items-center gap-4 text-sm">
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  Uptime: {formatUptime(healthReport.uptime_seconds)}
                </span>
              </div>
            </div>
            {healthReport.critical_issues?.length > 0 && (
              <div className="mt-3 pt-3 border-t border-white/30">
                <p className="font-medium mb-1">Problemas detectados:</p>
                <ul className="list-disc list-inside text-sm">
                  {healthReport.critical_issues.map((issue, i) => (
                    <li key={i}>{issue}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Settings Panel */}
      {showSettings && settings && isAdmin && (
        <div className="max-w-7xl mx-auto mb-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5" />
                Configuración del Monitoreo
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="flex items-center justify-between mb-4">
                    <span className="font-medium">Monitoreo Activo</span>
                    <Switch 
                      checked={settings.enabled}
                      onCheckedChange={(checked) => updateSettings({ ...settings, enabled: checked })}
                    />
                  </label>
                  <label className="flex items-center justify-between mb-4">
                    <span className="font-medium">Notificaciones Push</span>
                    <Switch 
                      checked={settings.notifications?.push ?? true}
                      onCheckedChange={(checked) => updateSettings({ 
                        ...settings, 
                        notifications: { ...settings.notifications, push: checked }
                      })}
                    />
                  </label>
                </div>
                <div>
                  <div className="mb-4">
                    <label className="font-medium block mb-2">
                      Intervalo de verificación: {settings.check_interval}s
                    </label>
                    <Slider 
                      value={[settings.check_interval]}
                      min={30}
                      max={600}
                      step={30}
                      onValueChange={([value]) => updateSettings({ ...settings, check_interval: value })}
                    />
                  </div>
                  <div>
                    <label className="font-medium block mb-2">
                      Cooldown entre alertas: {settings.alert_cooldown}s
                    </label>
                    <Slider 
                      value={[settings.alert_cooldown]}
                      min={60}
                      max={3600}
                      step={60}
                      onValueChange={([value]) => updateSettings({ ...settings, alert_cooldown: value })}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <div className="max-w-7xl mx-auto mb-6">
        <div className="flex gap-2 border-b">
          {(isAdmin ? ['overview', 'history', 'alerts'] : ['overview']).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 font-medium transition-colors ${
                activeTab === tab 
                  ? 'border-b-2 border-indigo-600 text-indigo-600' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab === 'overview' ? 'Vista General' : tab === 'history' ? 'Historial' : 'Alertas'}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto">
        {/* Overview Tab */}
        {activeTab === 'overview' && healthReport && (
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Memory */}
            <Card data-testid="memory-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-500 flex items-center gap-2">
                  <HardDrive className="w-4 h-4" />
                  Memoria
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-end justify-between">
                  <div>
                    <p className="text-2xl font-bold">{healthReport.system.memory.percent || 0}%</p>
                    <p className="text-xs text-gray-500">
                      {healthReport.system.memory.used_gb || 0} / {healthReport.system.memory.total_gb || 0} GB
                    </p>
                  </div>
                  {getStatusBadge(healthReport.system.memory.status)}
                </div>
                <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className={`h-full ${healthReport.system.memory.percent > 85 ? 'bg-red-500' : 'bg-green-500'}`}
                    style={{ width: `${healthReport.system.memory.percent || 0}%` }}
                  />
                </div>
              </CardContent>
            </Card>

            {/* CPU */}
            <Card data-testid="cpu-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-500 flex items-center gap-2">
                  <Cpu className="w-4 h-4" />
                  CPU
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-end justify-between">
                  <div>
                    <p className="text-2xl font-bold">{healthReport.system.cpu.percent || 0}%</p>
                    <p className="text-xs text-gray-500">Uso actual</p>
                  </div>
                  {getStatusBadge(healthReport.system.cpu.status)}
                </div>
                <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className={`h-full ${healthReport.system.cpu.percent > 90 ? 'bg-red-500' : 'bg-green-500'}`}
                    style={{ width: `${healthReport.system.cpu.percent || 0}%` }}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Disk */}
            <Card data-testid="disk-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-500 flex items-center gap-2">
                  <HardDrive className="w-4 h-4" />
                  Disco
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-end justify-between">
                  <div>
                    <p className="text-2xl font-bold">{healthReport.system.disk.percent || 0}%</p>
                    <p className="text-xs text-gray-500">
                      {healthReport.system.disk.used_gb || 0} / {healthReport.system.disk.total_gb || 0} GB
                    </p>
                  </div>
                  {getStatusBadge(healthReport.system.disk.status)}
                </div>
                <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className={`h-full ${healthReport.system.disk.percent > 90 ? 'bg-red-500' : 'bg-green-500'}`}
                    style={{ width: `${healthReport.system.disk.percent || 0}%` }}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Database */}
            <Card data-testid="database-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-500 flex items-center gap-2">
                  <Database className="w-4 h-4" />
                  Base de Datos
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-end justify-between">
                  <div>
                    <p className="text-2xl font-bold">
                      {healthReport.database.response_time_ms ? `${healthReport.database.response_time_ms}ms` : 'N/A'}
                    </p>
                    <p className="text-xs text-gray-500">
                      {healthReport.database.collections || 0} colecciones
                    </p>
                  </div>
                  {getStatusBadge(healthReport.database.status)}
                </div>
                <div className="mt-2 text-xs text-gray-500">
                  Almacenamiento: {healthReport.database.storage_size_mb || 0} MB
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="w-5 h-5" />
                Historial de Salud (24h)
              </CardTitle>
              <CardDescription>
                {history.length} verificaciones registradas
              </CardDescription>
            </CardHeader>
            <CardContent>
              {history.length === 0 ? (
                <p className="text-gray-500 text-center py-8">
                  No hay historial disponible. Ejecute una verificación manual para comenzar.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 px-3">Hora</th>
                        <th className="text-left py-2 px-3">Estado</th>
                        <th className="text-left py-2 px-3">Memoria</th>
                        <th className="text-left py-2 px-3">CPU</th>
                        <th className="text-left py-2 px-3">DB Response</th>
                        <th className="text-left py-2 px-3">Problemas</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.slice(-20).reverse().map((entry, i) => (
                        <tr key={i} className="border-b hover:bg-gray-50">
                          <td className="py-2 px-3">
                            {new Date(entry.timestamp).toLocaleTimeString()}
                          </td>
                          <td className="py-2 px-3">
                            {getStatusBadge(entry.overall_status)}
                          </td>
                          <td className="py-2 px-3">{entry.memory_percent}%</td>
                          <td className="py-2 px-3">{entry.cpu_percent}%</td>
                          <td className="py-2 px-3">{entry.db_response_ms}ms</td>
                          <td className="py-2 px-3">
                            {entry.critical_issues?.length || 0}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Alerts Tab */}
        {activeTab === 'alerts' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="w-5 h-5" />
                Historial de Alertas
              </CardTitle>
              <CardDescription>
                Últimas alertas enviadas por el sistema
              </CardDescription>
            </CardHeader>
            <CardContent>
              {alerts.length === 0 ? (
                <p className="text-gray-500 text-center py-8">
                  No se han enviado alertas. El sistema está funcionando correctamente.
                </p>
              ) : (
                <div className="space-y-3">
                  {alerts.map((alert, i) => (
                    <div key={i} className="border rounded-lg p-4 bg-red-50">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium text-red-800">
                            {new Date(alert.timestamp).toLocaleString()}
                          </p>
                          <ul className="mt-1 text-sm text-red-600">
                            {alert.issues?.map((issue, j) => (
                              <li key={j}>• {issue}</li>
                            ))}
                          </ul>
                        </div>
                        <Badge className="bg-red-100 text-red-800">
                          {alert.devices_notified} dispositivo(s)
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default HealthMonitorPage;
